import { defineMessage, useIntl } from 'react-intl';

import type { Flag } from '@townsquare/flags/types';
import { useAdminHubLinkForSite, useBillingPreviewUrl } from '@townsquare/url-utils';

import type { InviteToProjectResult } from './useInviteToProject';

const SUPPORT_CONTACT_URL = 'https://support.atlassian.com/contact/';

const errorFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.error-flag.title',
	defaultMessage: 'Something went wrong',
	description: 'Title of a message, shown when adding a user to the project fails',
});

const errorFlagDescriptionAdminMessage = defineMessage({
	id: 'townsquare.invite-user-modal.error-flag.description-admin',
	defaultMessage: 'Please try again.',
	description: 'The default error message when an unexpected error occurs, shown to site admins',
});

const errorFlagDescriptionMessage = defineMessage({
	id: 'townsquare.invite-user-modal.error-flag.description',
	defaultMessage: 'Please try again or contact your administrator for help.',
	description: 'The default error message when an unexpected error occurs',
});

const allRequestAccessAndInviteFailedFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.all-request-access-and-invite-failed-flag.title',
	defaultMessage: 'Something went wrong',
	description:
		'The title of a notification shown to users indicating that all invites and access requests failed to be sent',
});

const allRequestAccessAndInviteFailedFlagDescriptionMessage = defineMessage({
	id: 'townsquare.invite-user-modal.all-request-access-and-invite-failed-flag.description',
	defaultMessage:
		"We couldn't send any access requests or invitations. {isSiteAdmin, select, true{} other{Contact your admin for help.}}",
	description:
		'The content of a notification shown to users indicating that all invites and access requests failed to be sent',
});

const requestAccessFailureFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.request-access-failure-flag.title',
	defaultMessage: 'Something went wrong',
	description: 'The title of a notification shown to users indicating a request for access failed',
});

const requestAccessFailureFlagOneRequestFailedMessage = defineMessage({
	id: 'townsquare.invite-user-modal.request-access-failure-flag.one-request-access-failed',
	defaultMessage: "We couldn't request access for {email}. Contact your admin for help.",
	description:
		'The content of a notification shown to users indicating one request for access was not sent',
});

const requestAccessFailureFlagMultiRequestFailedMessage = defineMessage({
	id: 'townsquare.invite-user-modal.request-access-failure-flag.multi-request-access-failed',
	defaultMessage:
		"We couldn't complete your access {count, plural, one {request} other {requests}}. Contact your admin for help.",
	description:
		'The content of a notification shown to users indicating more than one request for access was not sent',
});

const inviteSuccessFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-success-flag.title',
	defaultMessage: '{invitedCount, plural, one {Invite} other {Invites}} sent',
	description:
		'The title of a notification shown to users indicating an invite has been successfully sent',
});

const inviteSuccessFlagOneInviteSentMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-success-flag.one-invite-sent',
	defaultMessage:
		"You've added {email} to your space. You'll see them on the board once they've been assigned a work item.",
	description:
		'The content of a notification shown to users indicating an invite has been sent to a single user',
});

const inviteSuccessFlagMultiInviteSentMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-success-flag.multi-invite-sent',
	defaultMessage:
		"You've added {invitedCount, plural, one {# person} other {# people}} to your space. You'll see them on the board once they've been assigned work items.",
	description:
		'The content of a notification shown to users indicating invites have been sent to multiple users',
});

const inviteSuccessFlagViewUsersMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-success-flag.view-users',
	defaultMessage: 'View users',
	description:
		'The link label on a notification shown to admins so that they may click to see users in project settings',
});

const requestAccessSuccessFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.request-access-success-flag.title',
	defaultMessage: "You've requested access for {count, plural, one {# user} other {# users}}",
	description:
		'The title of a notification shown to users indicating one or many requests for access have been sent and are awaiting approval',
});

const requestAccessSuccessFlagDescriptionMessage = defineMessage({
	id: 'townsquare.invite-user-modal.request-access-success-flag.description',
	defaultMessage:
		'They will be able to start collaborating when your admin approves their access request.',
	description:
		'The content of a notification shown to users indicating one or many requests for access have been sent and are awaiting approval',
});

const inviteFailedFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-flag.title',
	defaultMessage: '{failedCount, plural, one {# Invite} other {# Invites}} not sent',
	description:
		'The title of a notification shown to users indicating an invite failed due to tech issues',
});

const inviteFailedFlagOneInviteFailedMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-flag.one-invite-failed',
	defaultMessage: "We couldn't invite {email}.",
	description:
		'The content of a notification shown to users indicating the invitation failed to send',
});

const inviteFailedFlagManyInvitesFailedMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-flag.many-invites-failed',
	defaultMessage: "We couldn't send any invitations.",
	description: 'The content of a notification shown to users indicating no invitations were sent',
});

const inviteFailedFlagContactSupportMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-flag.contact-support',
	defaultMessage: 'Contact support',
	description:
		'The link label on a notification shown to users so that they may click to be shown a page and contact support',
});

const inviteFailedUserLimitFlagTitleMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-user-limit-flag.title',
	defaultMessage: '{failedCount, plural, one {# invite} other {# invites}} not sent',
	description:
		'The title of a notification shown to trusted users indicating some invites failed due to user limit reached',
});

const inviteFailedUserLimitFlagOneInviteFailedAdminMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-user-limit-flag.one-invite-failed-admin',
	defaultMessage: "We couldn't invite {email} because you've reached your user limit.",
	description:
		'The content of a notification shown to admins indicating one failed invite due to user limit reached',
});

const inviteFailedUserLimitFlagManyInvitesFailedAdminMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-user-limit-flag.many-invites-failed-admin',
	defaultMessage:
		"We couldn't send {failedCount, plural, one {# of your invite} other {# of your invites}} because you've reached your user limit.",
	description:
		'The content of a notification shown to admins indicating multiple failed invites due to user limit reached',
});

const inviteFailedUserLimitFlagInviteFailedTrustedUsersMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-user-limit-flag.invite-failed-trusted-users',
	defaultMessage:
		"We couldn't invite {failedCount, plural, one {# person} other {# people}} because you've reached your user limit. Contact your admin for help.",
	description:
		'The content of a notification shown to trusted users indicating some invites failed due to user limit reached',
});

const inviteFailedUserLimitFlagUpgradePlanMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-user-limit-flag.upgrade-plan',
	defaultMessage: 'Upgrade plan',
	description:
		'The link label on a notification shown to users so that they may click to load a new page to upgrade their products plan',
});

const inviteFailedUserLimitFlagContactSupportMessage = defineMessage({
	id: 'townsquare.invite-user-modal.invite-failed-user-limit-flag.contact-support',
	defaultMessage: 'Contact support',
	description:
		'The link label on a notification shown to users so that they may click to be shown a page and contact support',
});

export const useGetInviteToProjectFlags = () => {
	const { formatMessage } = useIntl();
	const adminUsersUrl = `${useAdminHubLinkForSite()}/users`;
	const adminBillingUrl = useBillingPreviewUrl();

	return (result: InviteToProjectResult, isSiteAdmin: boolean = false): Flag[] => {
		const { successfulInvites, failedInvites } = result;
		const finalFlags: Flag[] = [];

		if (failedInvites.length > 0) {
			const errorFlag = determinePrimaryErrorFlag(
				result,
				isSiteAdmin,
				formatMessage,
				adminBillingUrl,
			);
			finalFlags.push(errorFlag);

			if (successfulInvites.length === 0) {
				return finalFlags;
			}
		}

		const { invitedEmails, requestAccessCount } = successfulInvites.reduce(
			(acc, invite) => {
				const status = invite.response.inviteStatus;
				if (status === 'INVITED' || status === 'USER_EXISTS') {
					acc.invitedEmails.push(invite.email);
				} else if (status === 'INVITED_PENDING_APPROVAL' || status === 'PENDING_INVITE_EXISTS') {
					acc.requestAccessCount += 1;
				}
				return acc;
			},
			{ invitedEmails: [] as string[], requestAccessCount: 0 },
		);

		if (requestAccessCount > 0) {
			finalFlags.push(buildRequestAccessSuccessFlag(formatMessage, requestAccessCount));
			return finalFlags;
		}

		if (invitedEmails.length > 0) {
			finalFlags.push(
				buildInviteSuccessFlag(formatMessage, invitedEmails, isSiteAdmin, adminUsersUrl),
			);
		}

		return finalFlags;
	};
};

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const determinePrimaryErrorFlag = (
	result: InviteToProjectResult,
	isSiteAdmin: boolean,
	formatMessage: FormatMessage,
	adminBillingUrl: string,
): Flag => {
	const { failedInvites, successfulInvites } = result;

	const { userLimitEmails, standardEmails, requestAccessEmails } = failedInvites.reduce(
		(acc, failed) => {
			if (failed.status === 'LICENSE_LIMIT_REACHED') {
				acc.userLimitEmails.push(failed.email);
			} else if (failed.status === 'NOT_INVITED' || failed.error) {
				acc.standardEmails.push(failed.email);
			} else if (failed.status) {
				acc.requestAccessEmails.push(failed.email);
			}
			return acc;
		},
		{
			userLimitEmails: [] as string[],
			standardEmails: [] as string[],
			requestAccessEmails: [] as string[],
		},
	);

	const noSuccesses = successfulInvites.length === 0;
	const hasInviteErrors = standardEmails.length > 0 || userLimitEmails.length > 0;
	const hasRequestAccessErrors = requestAccessEmails.length > 0;

	if (noSuccesses && hasInviteErrors && hasRequestAccessErrors) {
		return buildAllRequestAccessAndInviteFailedFlag(formatMessage, isSiteAdmin);
	}

	if (userLimitEmails.length > 0) {
		return buildInviteFailedUserLimitFlag(
			formatMessage,
			userLimitEmails,
			isSiteAdmin,
			adminBillingUrl,
		);
	}

	if (standardEmails.length > 0) {
		return buildInviteFailedFlag(formatMessage, standardEmails);
	}

	if (requestAccessEmails.length > 0) {
		return buildRequestAccessFailureFlag(formatMessage, requestAccessEmails);
	}

	return buildErrorFlag(formatMessage, isSiteAdmin);
};

const buildErrorFlag = (formatMessage: FormatMessage, isSiteAdmin: boolean): Flag => ({
	id: 'generic-error-flag',
	type: 'error',
	shouldAutoDismiss: false,
	title: formatMessage(errorFlagTitleMessage),
	description: isSiteAdmin
		? formatMessage(errorFlagDescriptionAdminMessage)
		: formatMessage(errorFlagDescriptionMessage),
});

const buildAllRequestAccessAndInviteFailedFlag = (
	formatMessage: FormatMessage,
	isSiteAdmin: boolean,
): Flag => ({
	id: 'all-request-access-and-invite-failed-flag',
	type: 'error',
	shouldAutoDismiss: false,
	title: formatMessage(allRequestAccessAndInviteFailedFlagTitleMessage),
	description: formatMessage(allRequestAccessAndInviteFailedFlagDescriptionMessage, {
		isSiteAdmin,
	}),
});

const buildRequestAccessFailureFlag = (formatMessage: FormatMessage, emails: string[]): Flag => ({
	id: 'request-access-failure-flag',
	type: 'error',
	shouldAutoDismiss: false,
	title: formatMessage(requestAccessFailureFlagTitleMessage),
	description:
		emails.length === 1
			? formatMessage(requestAccessFailureFlagOneRequestFailedMessage, { email: emails[0] })
			: formatMessage(requestAccessFailureFlagMultiRequestFailedMessage, {
					count: emails.length,
				}),
});

const buildInviteSuccessFlag = (
	formatMessage: FormatMessage,
	invitedEmails: string[],
	isSiteAdmin: boolean,
	adminUsersUrl: string,
): Flag => {
	const invitedCount = invitedEmails.length;

	return {
		id: 'invite-success-flag',
		type: 'success',
		title: formatMessage(inviteSuccessFlagTitleMessage, { invitedCount }),
		description:
			invitedCount === 1
				? formatMessage(inviteSuccessFlagOneInviteSentMessage, { email: invitedEmails[0] })
				: formatMessage(inviteSuccessFlagMultiInviteSentMessage, { invitedCount }),
		actions: isSiteAdmin
			? [
					{
						content: formatMessage(inviteSuccessFlagViewUsersMessage),
						onClick: () => window.open(adminUsersUrl, '_blank'),
					},
				]
			: [],
	};
};

const buildRequestAccessSuccessFlag = (
	formatMessage: FormatMessage,
	requestAccessCount: number,
): Flag => ({
	id: 'request-access-success-flag',
	type: 'warning',
	title: formatMessage(requestAccessSuccessFlagTitleMessage, { count: requestAccessCount }),
	description: formatMessage(requestAccessSuccessFlagDescriptionMessage),
});

const buildInviteFailedFlag = (formatMessage: FormatMessage, emails: string[]): Flag => {
	const failedCount = emails.length;

	return {
		id: 'invite-failed-flag',
		type: 'error',
		shouldAutoDismiss: false,
		title: formatMessage(inviteFailedFlagTitleMessage, { failedCount }),
		description:
			failedCount === 1
				? formatMessage(inviteFailedFlagOneInviteFailedMessage, { email: emails[0] })
				: formatMessage(inviteFailedFlagManyInvitesFailedMessage),
		actions: [
			{
				content: formatMessage(inviteFailedFlagContactSupportMessage),
				onClick: () => window.open(SUPPORT_CONTACT_URL, '_blank'),
			},
		],
	};
};

const getInviteFailedUserLimitDescription = (
	formatMessage: FormatMessage,
	emails: string[],
	isSiteAdmin: boolean,
	failedCount: number,
): string => {
	if (!isSiteAdmin) {
		return formatMessage(inviteFailedUserLimitFlagInviteFailedTrustedUsersMessage, {
			failedCount,
		});
	}
	if (failedCount === 1) {
		return formatMessage(inviteFailedUserLimitFlagOneInviteFailedAdminMessage, {
			email: emails[0],
		});
	}
	return formatMessage(inviteFailedUserLimitFlagManyInvitesFailedAdminMessage, { failedCount });
};

const buildInviteFailedUserLimitFlag = (
	formatMessage: FormatMessage,
	emails: string[],
	isSiteAdmin: boolean,
	adminBillingUrl: string,
): Flag => {
	const failedCount = emails.length;

	return {
		id: 'invite-failed-user-limit-reached-flag',
		type: 'error',
		shouldAutoDismiss: false,
		title: formatMessage(inviteFailedUserLimitFlagTitleMessage, { failedCount }),
		description: getInviteFailedUserLimitDescription(
			formatMessage,
			emails,
			isSiteAdmin,
			failedCount,
		),
		actions: isSiteAdmin
			? [
					{
						content: formatMessage(inviteFailedUserLimitFlagUpgradePlanMessage),
						onClick: () => window.open(adminBillingUrl, '_blank'),
					},
					{
						content: formatMessage(inviteFailedUserLimitFlagContactSupportMessage),
						onClick: () => window.open(SUPPORT_CONTACT_URL, '_blank'),
					},
				]
			: [],
	};
};
